import { useAuth } from "@/hooks/auth";
import { useMarketProducersQuery } from "@/hooks/metadata";
import { usePreferenceMutation } from "@/hooks/preference";
import Button from "@ingka/button";
import HyperLink from "@ingka/hyperlink";
import Modal, { ModalFooter, Prompt } from "@ingka/modal";
import Select, { Option } from "@ingka/select";
import Text from "@ingka/text";
import { useState } from "react";
import { Loading } from "../Loading/Loading";

type BelongsToMarketPromptProps = {
  onSuccess: () => void;
};

export function BelongsToMarketPrompt({
  onSuccess,
}: BelongsToMarketPromptProps) {
  const [preference, setPreference] = useState<number>(-1);
  const marketProducersQuery = useMarketProducersQuery();
  const preferenceMutation = usePreferenceMutation();
  const [isError, setIsError] = useState<boolean | null>(null);
  const { id: userId } = useAuth().user;

  if (marketProducersQuery.isError) {
    throw new Error("Error loading market producers metadata");
  }

  if (!marketProducersQuery.isSuccess) {
    return <Loading text="Loading market producers metadata" />;
  }

  const savePreference = async () => {
    setIsError(false);
    for (let i = 1; i <= 3; i++) {
      try {
        if (preference < 0) {
          return;
        }
        await preferenceMutation.mutateAsync({
          userId,
          belongsToMarket: preference,
        });
        onSuccess();
        return;
      } catch {
        // Delay before retrying
        await new Promise((resolve) => setTimeout(resolve, 500 * i));
      }
    }
    setIsError(true);
  };

  return (
    <Modal visible={true} escapable={false} handleCloseBtn={() => {}}>
      <Prompt
        title={
          <Text headingSize="m" className="mt-space100">
            What market do you belong to?
          </Text>
        }
        titleId="user-preference"
        header={null}
        footer={
          <ModalFooter>
            <Button
              text={isError === false ? "Saving ..." : "Save choice"}
              type="primary"
              disabled={preference < 0 || isError === false}
              onClick={savePreference}
            />
          </ModalFooter>
        }
      >
        <Text bodySize="l" className="mb-space150">
          For us to be able to handle your upload request as fast and
          effectively as possible we need to know in what market you are working
          for. This makes it possible for us to assign the request to the
          correct approver. You will only have to fill this information out
          once. If changes need to be made, please contact us via the{" "}
          <HyperLink
            url="https://jira.digital.ingka.com/servicedesk/customer/portal/16/create/7009"
            newWindow
            text="support link "
          />
          and we will help you.
        </Text>
        <Select
          id="select-preference"
          label="Select a market"
          onChange={(e) => setPreference(+e.target.value)}
        >
          {marketProducersQuery.data.map((producer) => (
            <Option
              key={producer.id}
              value={`${producer.id}`}
              name={producer.name}
            />
          ))}
        </Select>
        {isError && (
          <Text bodySize="m" className="mt-space150 text-red-500">
            Something went wrong saving your selection. Please try again or
            contact ingka dam support.
          </Text>
        )}
      </Prompt>
    </Modal>
  );
}
